'use client';

import { useEffect, useState } from 'react';
import {
  captureCrashAndGetReference,
  CRASH_SCREEN_HEADING,
  CRASH_SCREEN_BODY_WITH_CODE,
  CRASH_SCREEN_BODY_NO_CODE,
} from '@/utils/observability/crashReference';

/**
 * Root-layout crashes only — app/error.tsx shadows this for every route.
 *
 * Kept behaviourally identical to app/error.tsx (#3253): same reference-code
 * rules from the shared helper, same copy. A user cannot tell which of the two
 * screens they are looking at, and the assistant's `page_error_reference_code`
 * entry describes a single behaviour, so the two must not diverge.
 *
 * The old copy here promised "Our team has been notified." That claim is not
 * provable — it is false outside production, false with no DSN, and false
 * behind a browser add-on that blocks the ingest domain — so it is gone from
 * both screens.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [reference, setReference] = useState<string | null>(null);

  useEffect(() => {
    console.error(error);
    setReference(captureCrashAndGetReference(error));
  }, [error]);

  return (
    <html>
      <body>
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="max-w-md w-full space-y-8">
            <div>
              <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
                {CRASH_SCREEN_HEADING}
              </h2>
              <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
                {reference ? CRASH_SCREEN_BODY_WITH_CODE : CRASH_SCREEN_BODY_NO_CODE}
              </p>
              {reference && (
                <p className="mt-4 text-center text-xs text-gray-500 dark:text-gray-500">
                  Reference code:{' '}
                  <span className="font-mono select-all break-all">{reference}</span>
                </p>
              )}
            </div>
            <div className="text-center">
              <button
                onClick={reset}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
